// Cost model + formatting.
//
// Three cost numbers exist per row:
//
//   Unit Cost   — the cost field that comes back from the Orcanos filter.
//   Cost        — quantity × unit cost (the row's own extended cost).
//   Total Cost  — Cost + quantity × the cost of everything inside it.
//
// The rollup is computed from rows we already have in memory — it never
// triggers a fetch. In the grid that means a sub-assembly whose children
// haven't been expanded yet reports a PARTIAL total (flagged so the UI can
// mark it); "Expand all" (⤓) or an export makes it exact.

/**
 * Format a number as a cost string: 9,999,999.00
 * Deterministic — does not depend on browser locale.
 */
export function fmtCost(n) {
  const fixed = Number(n).toFixed(2);
  const [int, dec] = fixed.split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
}

/** Matches any column whose title or key contains "cost" or "price". */
const COST_RE = /cost|price/i;

export function isCostColumn(c) {
  return (
    COST_RE.test(String(c.title || '')) ||
    COST_RE.test(String(c.key || ''))
  );
}

/**
 * Given a raw cell value (possibly containing HTML or currency symbols),
 * parse it as a number and return a formatted cost string.
 * Returns the original value unchanged if it doesn't parse as a finite number.
 */
export function formatCostValue(raw) {
  const stripped = String(raw).replace(/<[^>]+>/g, '').trim();
  if (!stripped) return raw;
  const num = parseFloat(stripped.replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(num)) return raw;
  return fmtCost(num);
}

// ─── Synthetic column keys ─────────────────────────────────────────────────
export const UNIT_COST_KEY = '__unitCost';
export const COST_KEY = '__cost';
export const TOTAL_COST_KEY = '__totalCost';

export const COST_COLUMN_KEYS = new Set([UNIT_COST_KEY, COST_KEY, TOTAL_COST_KEY]);

// ─── Unit cost field resolution ────────────────────────────────────────────
// Preferred titles/names, in priority order. Matched exactly (normalized) so
// unrelated fields like "Cost Center" don't win. If none match we fall back to
// the first field whose Title/Name mentions cost or price — the same loose rule
// the summary export has always used, kept so both agree on the same number.
const UNIT_COST_NAMES = [
  'unit cost',
  'unit price',
  'cost',
  'price',
  'standard cost',
  'item cost',
  'part cost',
];

function norm(s) {
  return String(s || '').toLowerCase().replace(/[\s_]+/g, ' ').trim();
}

function fieldText(f) {
  return (f && (f.Display_text || f.Display || f.Text || f.Value || '')) || '';
}

/** Returns the Field object that holds the unit cost, or null. */
export function findUnitCostField(fields) {
  const list = Array.isArray(fields) ? fields : [];
  for (const want of UNIT_COST_NAMES) {
    const f = list.find((x) => norm(x.Title) === want || norm(x.Name) === want);
    if (f) return f;
  }
  return list.find((x) => COST_RE.test(x.Title || x.Name || '')) || null;
}

/** True when the row's filter exposes a unit-cost field at all. */
export function hasUnitCostField(row) {
  return !!findUnitCostField(row?.fields);
}

function parseNum(raw) {
  const stripped = String(raw ?? '').replace(/<[^>]+>/g, '').trim();
  if (!stripped) return NaN;
  return parseFloat(stripped.replace(/[^\d.-]/g, ''));
}

/** Numeric unit cost for a row. 0 when absent or unparseable. */
export function getUnitCost(row) {
  const num = parseNum(fieldText(findUnitCostField(row?.fields)));
  return Number.isFinite(num) && num > 0 ? num : 0;
}

/**
 * Numeric quantity for a row.
 *
 * A quantity that is **missing or unparseable** counts as 1, so a blank cell
 * never zeroes out a real cost — which is also how root BOMs work, since a PRT
 * carries no Quantity field and is a single unit by definition. `isRoot` is
 * accepted for call-site clarity but the row's own value always wins: a
 * sub-assembly exported as its own root keeps the quantity the grid showed for
 * it, so the two agree on Total Cost.
 *
 * An **explicit zero is zero**. Real BOMs carry qty-0 lines (a part listed for
 * reference, or dropped from the build), and rounding those up to 1 charges the
 * BOM for parts it doesn't consume — a qty-0 line at 13.11 under a qty-14
 * parent silently added 183.54. Negative quantities clamp to 0.
 */
export function getQuantity(row, isRoot) {
  const raw = row?.byName?.('Quantity') || row?.byTitle?.('Quantity') || '';
  const num = parseNum(raw);
  if (!Number.isFinite(num)) return 1;   // blank / unreadable → assume one
  return num > 0 ? num : 0;              // an explicit 0 stays 0
}

/**
 * Roll costs up a flat, parent-before-child node list.
 *
 * Node shape: { nodeKey, parentKey, row, isRoot } plus, in the live tree,
 * { expanded, loaded, childCount }.
 *
 * Returns Map<nodeKey, { unit, qty, cost, total, complete }> where
 *   cost     = qty × unit                     (this row's own material)
 *   total    = qty × (unit + contents)        (itself plus everything inside)
 *   complete = every descendant that exists is represented in `total`
 *
 * **A row's own cost is ALWAYS added to its children's.** This is the confirmed
 * business rule — do not "fix" it.
 *
 *     perUnit(row) = its own unit cost + Σ children (qty × perUnit)
 *
 * ⚠️ It will look like double counting, and it was changed to leaves-only once
 * on exactly that reasoning. Do not repeat that. In the Covaris data an
 * assembly's stored cost happens to track the sum of its parts closely — for
 * 520377KIT the root stored 3,384.66 while its parts computed to 3,407.16, and
 * one sub-assembly matched its parts to the penny — which reads like a
 * roll-up. It is not treated as one. The product owner confirmed the rule twice
 * against a real BOM: a part contributes its own cost and its contents, and the
 * kit above totals ~10,200, not ~3,400. If the stored figures ever need
 * reconciling, that is a data question for Covaris, not a change to this
 * formula.
 *
 * **Quantities multiply down the tree.** A child's Quantity is per ONE unit of
 * its parent, so a qty-2 sub-assembly consumes twice its contents:
 *
 *     Sub-assembly  qty 2 × unit 5.00   cost 10.00   total 52.00
 *     └── Screw     qty 3 × unit 7.00   cost 21.00   total 21.00
 *                                       2 × (5.00 + 21.00) = 52.00
 *
 * Every row is therefore stated **per one unit of its parent** — the screw row
 * reads 21.00 even though six screws (2 × 3) end up in the BOM. Expanded, the
 * root's total is Σ over EVERY node of (effective qty × unit), where effective
 * qty is the product of the quantities along the path. `buildSummary()` in
 * exportUtils relies on that identity; it excludes the export root, so a
 * Summary grand total is the hierarchic one minus the root's own cost.
 *
 * **`total` is only meaningful when `complete` is true.** An incomplete total
 * is a floor, not an answer, and the UI must not present it as a number — see
 * `resolveSubtreeCost()` in `useBomChildren.js` for how a row gets resolved.
 *
 * Options:
 *   assumeComplete — for exports, where the whole subtree was fetched up front
 *                    and nothing can be missing. Leafness is then read off the
 *                    list itself (a node nothing calls parent), since export
 *                    nodes carry no probe flags.
 *   exactSubtree   — Map<originalId, number> from `resolveSubtreeCost()`: the
 *                    cost of the contents of ONE unit of that part (its own
 *                    unit cost is added on top here). A node whose part is here is
 *                    authoritative and counts as complete whatever is loaded
 *                    beneath it. Keyed by *part*, so the value is valid
 *                    wherever that part is used — resolving one occurrence
 *                    resolves them all.
 */
export function computeCostRollup(
  nodes,
  { assumeComplete = false, exactSubtree = null } = {}
) {
  const out = new Map();
  const childSum = new Map();      // nodeKey -> Σ children total
  const childComplete = new Map(); // nodeKey -> all children complete?

  // Export nodes have no probe flags, so leafness comes from the list: a node
  // that nothing names as its parent has no children. Only safe when the whole
  // subtree is present, which is exactly what assumeComplete asserts.
  const parentKeys = assumeComplete
    ? new Set(nodes.map((n) => n.parentKey).filter(Boolean))
    : null;

  // Reverse order: children always follow their parent in the flat list, so a
  // single backwards pass has every child's total ready before its parent.
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    const unit = getUnitCost(n.row);
    const qty = getQuantity(n.row, n.isRoot);
    const cost = unit * qty;

    const hasExact = !!exactSubtree?.has(n.row?.originalId);
    const exact = hasExact ? exactSubtree.get(n.row.originalId) : undefined;

    // A row is a known leaf once the probe pass or an expand told us so; in an
    // export, once the full list says nothing hangs off it.
    const knownLeaf = parentKeys
      ? !parentKeys.has(n.nodeKey)
      : n.row?.hasChildren === false || (n.loaded && n.childCount === 0);

    // Cost of ONE unit of this row's part: its own material PLUS its contents.
    // A leaf simply has no contents. See the rule note above before changing.
    const contents = hasExact
      ? (typeof exact === 'number' ? exact : 0)
      : childSum.get(n.nodeKey) || 0;
    const perUnit = unit + contents;

    // `qty ×` is the BOM part: a child's Quantity is "per ONE unit of the
    // parent", so two of an assembly means two of everything inside it.
    const total = qty * perUnit;

    let complete;
    if (assumeComplete || hasExact) {
      complete = true;
    } else {
      const kidsComplete = childComplete.get(n.nodeKey) ?? true;
      // A collapsed-but-loaded row counts as incomplete: its descendants
      // aren't in the list, so they aren't in `total`.
      const childrenPresent = n.expanded && n.loaded;
      complete = knownLeaf || (childrenPresent && kidsComplete);
    }

    out.set(n.nodeKey, { unit, qty, cost, total, complete });

    if (n.parentKey) {
      childSum.set(n.parentKey, (childSum.get(n.parentKey) || 0) + total);
      childComplete.set(
        n.parentKey,
        (childComplete.get(n.parentKey) ?? true) && complete
      );
    }
  }
  return out;
}
